/**
 * Shared number formatters (UI Design Standards §5.3).
 *
 * en-US thousands separators, a true minus sign (U+2212) for negatives, and
 * EM_DASH for null, undefined, empty string or anything that isn't a number.
 * New code uses formatHrs; the legacy utils/formatHours.js ("8h") stays for
 * existing screens.
 */
import { EM_DASH } from './formatDate';

const MINUS = '−';

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const formatters = new Map();
const getFormatter = (minDecimals, maxDecimals) => {
  const key = `${minDecimals}-${maxDecimals}`;
  if (!formatters.has(key)) {
    formatters.set(
      key,
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
      })
    );
  }
  return formatters.get(key);
};

// Formats |n| and adds the sign, so a value that rounds to zero gets no sign.
const formatSigned = (n, minDecimals, maxDecimals, signed = false) => {
  const text = getFormatter(minDecimals, maxDecimals).format(Math.abs(n));
  const roundsToZero = /^[0.,]+$/.test(text);
  if (n < 0 && !roundsToZero) return `${MINUS}${text}`;
  if (signed && n > 0 && !roundsToZero) return `+${text}`;
  return text;
};

/**
 * e.g. 32335.5 → "32,335.5", -14685 → "−14,685", 843 (signed) → "+843".
 * @param {number|string} value
 * @param {{ decimals?: number, signed?: boolean }} [options]
 *   decimals: fixed decimal places (default: up to 1); signed: "+" on positives
 * @returns {string}
 */
export const formatNumber = (value, { decimals, signed = false } = {}) => {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  return decimals === undefined
    ? formatSigned(n, 0, 1, signed)
    : formatSigned(n, decimals, decimals, signed);
};

/**
 * Hours: whole numbers without decimals, fractional to 1 decimal.
 * e.g. 8 → "8", 8.5 → "8.5", 1784 → "1,784", 556 (withUnit) → "556 hrs".
 * @param {number|string} value
 * @param {{ withUnit?: boolean, zeroAsDash?: boolean }} [options]
 * @returns {string}
 */
export const formatHrs = (value, { withUnit = false, zeroAsDash = false } = {}) => {
  const n = toNumber(value);
  if (n === null || (zeroAsDash && n === 0)) return EM_DASH;
  const decimals = Number.isInteger(n) ? 0 : 1;
  const text = formatSigned(n, decimals, decimals);
  return withUnit ? `${text} hrs` : text;
};

/**
 * Percent, where value is already a percent (60 = 60%).
 * e.g. 112 → "112%", 117.9 (decimals 1) → "117.9%", 661225 → ">999%".
 * @param {number|string} value
 * @param {{ decimals?: number, base?: number }} [options]
 *   base: the denominator, when known; zero or negative shows "—"
 * @returns {string}
 */
export const formatPercent = (value, { decimals = 0, base } = {}) => {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  if (base !== undefined) {
    const b = toNumber(base);
    if (b === null || b <= 0) return EM_DASH;
  }
  const factor = 10 ** decimals;
  if (Math.round(n * factor) / factor > 999) return '>999%';
  return `${formatSigned(n, decimals, decimals)}%`;
};
